import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CloseIcon from "@mui/icons-material/Close";

/**
 * Renders a JSON object (log record or dsokOrders document) pretty-printed.
 * title - modal title; fileName - download file name without extension.
 */
const CreatedOrderLogDetailModal = ({ open, onClose, item, title, fileName }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const fullJson = item != null ? JSON.stringify(item, null, 2) : "";

  const handleCopy = () => {
    if (!navigator.clipboard || !fullJson) return;
    navigator.clipboard.writeText(fullJson).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownload = () => {
    if (!fullJson) return;
    // Názov súboru podľa orderId (fallback _id), aby sa dal záznam ľahko dohľadať.
    const idPart = item?.orderId || item?._id || "log";
    const blob = new Blob([fullJson], { type: "application/json" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileName || `created-order-log-${idPart}`}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {title || t("pages.createdOrdersLogs.modal.title")}
        <IconButton
          onClick={onClose}
          size="small"
          aria-label={t("pages.createdOrdersLogs.buttons.close")}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            p: 1.5,
            bgcolor: "action.hover",
            borderRadius: 1,
            maxHeight: 420,
            overflow: "auto",
            position: "relative",
          }}
        >
          <Tooltip
            title={t("pages.createdOrdersLogs.buttons.download", {
              defaultValue: "Stiahnuť JSON",
            })}
          >
            <IconButton
              size="small"
              onClick={handleDownload}
              sx={{ position: "absolute", top: 4, right: 40, zIndex: 1 }}
            >
              <FileDownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              copied
                ? t("pages.createdOrdersLogs.buttons.copied")
                : t("pages.createdOrdersLogs.buttons.copy")
            }
          >
            <IconButton
              size="small"
              onClick={handleCopy}
              sx={{ position: "absolute", top: 4, right: 4, zIndex: 1 }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <pre
            style={{
              margin: 0,
              fontFamily: "monospace",
              fontSize: "0.8rem",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              paddingRight: 72,
            }}
          >
            {item ? fullJson : ""}
          </pre>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("pages.createdOrdersLogs.buttons.close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatedOrderLogDetailModal;
